import { Component } from '@theme/component';

class FestiveHero extends Component {
  #slides = [];
  #index = 0;
  #timer;
  #transitionTimer;
  #abort;
  #observer;
  #motion;
  #paused = false;
  #hovered = false;
  #focused = false;
  #visible = true;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    clearTimeout(this.#timer);
    clearTimeout(this.#transitionTimer);
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #setup() {
    this.#cleanup();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#slides = [...this.querySelectorAll('[data-festive-slide]')];
    if (!this.#slides.length) return;
    this.#index = Math.min(this.#index, this.#slides.length - 1);
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused = this.dataset.autoplay !== 'true';
    this.#hovered = false;
    this.#focused = this.contains(document.activeElement);
    for (const slide of this.#slides) slide.hidden = false;
    this.querySelector('[data-festive-controls]')?.removeAttribute('hidden');
    this.dataset.enhanced = 'true';
    this.#show(this.#index);

    this.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      this.#hovered = true;
      this.#schedule();
    }, options);
    this.addEventListener('pointerleave', () => {
      this.#hovered = false;
      this.#schedule();
    }, options);
    this.addEventListener('focusin', () => {
      this.#focused = true;
      this.#schedule();
    }, options);
    this.addEventListener('focusout', event => {
      this.#focused = this.contains(event.relatedTarget);
      this.#schedule();
    }, options);
    document.addEventListener('visibilitychange', () => this.#schedule(), options);
    this.#motion.addEventListener('change', () => {
      this.#updatePlayButton();
      this.#schedule();
    }, options);
    document.addEventListener('shopify:block:select', event => {
      const index = this.#slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
      if (index < 0) return;
      this.#paused = true;
      this.#show(index, true);
    }, options);
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(entries => {
        this.#visible = entries[0].isIntersecting;
        this.#schedule();
      }, { threshold: 0.15 });
      this.#observer.observe(this);
    }
    this.#schedule();
  }

  #show(index, announce = false) {
    const next = (index + this.#slides.length) % this.#slides.length;
    const previous = this.#slides[this.#index];
    const transitioning = next !== this.#index && !this.#motion.matches;
    clearTimeout(this.#transitionTimer);
    this.#index = next;
    for (const [i, slide] of this.#slides.entries()) {
      const active = i === this.#index;
      slide.dataset.leaving = String(transitioning && slide === previous);
      slide.dataset.active = String(active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    }
    if (transitioning) this.#transitionTimer = setTimeout(() => {
      previous.dataset.leaving = 'false';
    }, 1100);
    for (const button of this.querySelectorAll('[data-festive-index]')) {
      button.setAttribute('aria-pressed', String(Number(button.dataset.festiveIndex) === this.#index));
    }
    this.dataset.currentSlide = String(this.#index + 1);
    if (announce) {
      const status = this.querySelector('[data-festive-status]');
      if (status) status.textContent = this.#slides[this.#index].dataset.announcement;
    }
    this.#updatePlayButton();
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    const running = this.#slides.length > 1 && !this.#paused && !this.#motion.matches && !this.#hovered && !this.#focused && !document.hidden && this.#visible;
    this.dataset.rotating = String(running);
    if (running) this.#timer = setTimeout(() => this.#show(this.#index + 1), Number(this.dataset.interval) * 1000 || 7000);
  }

  #updatePlayButton() {
    const button = this.querySelector('[data-festive-play]');
    if (!button) return;
    const paused = this.#paused || this.#motion.matches;
    button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    button.dataset.paused = String(paused);
    button.disabled = this.#motion.matches;
  }

  selectSlide(event) {
    const button = event.target.closest('[data-festive-index]');
    if (button) this.#show(Number(button.dataset.festiveIndex), true);
  }

  stepSlide(event) {
    const button = event.target.closest('[data-festive-step]');
    if (button) this.#show(this.#index + Number(button.dataset.festiveStep), true);
  }

  togglePlay() {
    if (this.#motion.matches) return;
    this.#paused = !this.#paused;
    this.#updatePlayButton();
    this.#schedule();
  }

  handleKeydown(event) {
    const target = event.composedPath()[0];
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !target.closest('[data-festive-controls]')) return;
    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    let index = this.#index + (event.key === 'ArrowRight' ? 1 : -1) * rtl;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = this.#slides.length - 1;
    this.#show(index, true);
    this.querySelector(`[data-festive-index="${this.#index}"]`)?.focus();
  }
}

if (!customElements.get('zfest-hero')) customElements.define('zfest-hero', FestiveHero);
